/**
 * Live follow-route metrics (spec 05): XTE, distance/bearing to the next
 * waypoint, and an ETA from actual SOG. Auto-advances the active leg with
 * hysteresis (advance within 50 m, re-arm past 100 m).
 */
import { createStore } from 'zustand/vanilla';
import {
  haversineNm,
  crossTrackErrorNm,
  rhumbBearingDeg,
  rhumbDistanceNm,
  METERS_PER_NM,
} from '@boatnav/core';
import { posStore, type Fix } from '../nav/posStore.js';
import { useFollowStore } from './followStore.js';
import type { Waypoint } from '../../lib/db.js';

export interface FollowMetrics {
  xteNm: number; // signed: + starboard, − port
  toWpNm: number;
  toWpBrg: number;
  etaMs: number | null;
}

export const followMetricsStore = createStore<{ metrics: FollowMetrics | null }>(() => ({
  metrics: null,
}));

const ADVANCE_M = 50;
const REARM_M = 100;
let canAdvance = true;
let unsub: (() => void) | null = null;

function remainingAfter(waypoints: Waypoint[], legIndex: number): number {
  let sum = 0;
  for (let i = legIndex + 1; i < waypoints.length; i++) {
    sum += rhumbDistanceNm(waypoints[i - 1]!, waypoints[i]!);
  }
  return sum;
}

function onFix(fix: Fix): void {
  const { route, activeLeg, setActiveLeg } = useFollowStore.getState();
  if (!route || route.waypoints.length < 2) {
    followMetricsStore.setState({ metrics: null });
    return;
  }

  const wps = route.waypoints;
  const leg = Math.min(activeLeg, wps.length - 1);
  const start = wps[leg - 1]!;
  const end = wps[leg]!;
  const here = { lat: fix.lat, lon: fix.lon };

  const xteNm = crossTrackErrorNm(here, start, end);
  const toWpNm = haversineNm(here, end);
  const toWpBrg = rhumbBearingDeg(here, end);

  // Auto-advance with hysteresis.
  const toWpM = toWpNm * METERS_PER_NM;
  if (toWpM > REARM_M) canAdvance = true;
  if (toWpM < ADVANCE_M && canAdvance && leg < wps.length - 1) {
    setActiveLeg(leg + 1);
    canAdvance = false;
  }

  // ETA from actual SOG (fix.sogKn is already EMA-smoothed in positionService).
  let etaMs: number | null = null;
  if (fix.sogKn != null && fix.sogKn >= 0.5) {
    const remainingNm = toWpNm + remainingAfter(wps, leg);
    etaMs = Date.now() + (remainingNm / fix.sogKn) * 3_600_000;
  }

  followMetricsStore.setState({ metrics: { xteNm, toWpNm, toWpBrg, etaMs } });
}

export function startFollowMetrics(): void {
  if (unsub) return;
  canAdvance = true;
  const current = posStore.getState().fix;
  if (current) onFix(current);
  unsub = posStore.subscribe((s, prev) => {
    if (s.fix !== prev.fix && s.fix) onFix(s.fix);
  });
}

export function stopFollowMetrics(): void {
  unsub?.();
  unsub = null;
  followMetricsStore.setState({ metrics: null });
}
